import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";
import { ArrowRight, Mail, Phone, MapPin, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL;

export default function Contact() {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState("form"); // "form" | "calendly"

  // Lazy-load Calendly widget script when user opens the calendar tab
  useEffect(() => {
    if (mode !== "calendly" || !CALENDLY_URL) return;
    const existing = document.querySelector('script[src*="calendly.com/assets/external/widget.js"]');
    if (existing) return;
    const s = document.createElement("script");
    s.src = "https://assets.calendly.com/assets/external/widget.js";
    s.async = true;
    document.body.appendChild(s);
    const link = document.createElement("link");
    link.href = "https://assets.calendly.com/assets/external/widget.css";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }, [mode]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, source: "contact_form" });
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch {
      toast.error(t("contact.error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-36 bg-[#F7F5F0]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="mb-12 md:mb-20">
          <div className="eyebrow text-ink mb-4">{t("contact.overline")} · 08</div>
          <h2
            className="heading-display leading-[0.9] tracking-tight"
            data-testid="contact-title"
            style={{ fontSize: "clamp(3rem, 13vw, 13rem)" }}
          >
            {t("contact.title")}
          </h2>
        </div>

        <div className="grid grid-cols-12 gap-y-10 md:gap-x-16 md:gap-y-16">
          {/* Left info */}
          <div className="col-span-12 md:col-span-4">
            <p className="text-lg text-ink max-w-md mb-10 leading-relaxed">
              {t("contact.sub")}
            </p>

            <ul className="space-y-5">
              <li className="flex gap-3 items-start" data-testid="contact-email">
                <Mail size={18} className="mt-1" />
                <a href="mailto:info@ghio.nl" className="underline decoration-1 underline-offset-4 hover:text-navy">
                  info@ghio.nl
                </a>
              </li>
              <li className="flex gap-3 items-start" data-testid="contact-phone">
                <Phone size={18} className="mt-1" />
                <a href="tel:+31653731819" className="hover:text-navy">
                  +31 6 53 73 18 19
                </a>
              </li>
              <li className="flex gap-3 items-start" data-testid="contact-address">
                <MapPin size={18} className="mt-1" />
                <span>
                  Houtsaachmole 3C
                  <br />
                  8531 WC Lemmer
                  <br />
                  Nederland
                </span>
              </li>
            </ul>
          </div>

          {/* Right side: form / Calendly */}
          <div className="col-span-12 md:col-span-8 min-w-0">
            {CALENDLY_URL && (
              <div
                data-testid="contact-mode-switch"
                className="mb-6 inline-flex items-center border border-navy/20 bg-white"
              >
                <button
                  type="button"
                  data-testid="contact-mode-form"
                  onClick={() => setMode("form")}
                  className={`px-4 py-3 min-h-[44px] text-xs tracking-wide transition-colors ${
                    mode === "form"
                      ? "bg-navy text-[#F7F5F0]"
                      : "text-ink hover:text-navy"
                  }`}
                >
                  <Mail size={14} className="inline mr-2 -mt-0.5" />
                  {lang === "nl" ? "Stuur bericht" : "Send a message"}
                </button>
                <button
                  type="button"
                  data-testid="contact-mode-calendly"
                  onClick={() => setMode("calendly")}
                  className={`px-4 py-3 min-h-[44px] text-xs tracking-wide transition-colors ${
                    mode === "calendly"
                      ? "bg-navy text-[#F7F5F0]"
                      : "text-ink hover:text-navy"
                  }`}
                >
                  <Calendar size={14} className="inline mr-2 -mt-0.5" />
                  {lang === "nl" ? "Plan een call (15 min)" : "Book a call (15 min)"}
                </button>
              </div>
            )}

            {mode === "calendly" && CALENDLY_URL ? (
              <div
                data-testid="calendly-embed"
                className="w-full max-w-full border border-navy bg-white p-2 md:p-3 overflow-hidden"
                style={{ maxWidth: "calc(100vw - 48px)" }}
              >
                <div
                  className="calendly-inline-widget"
                  data-url={`${CALENDLY_URL}?hide_gdpr_banner=1&background_color=ffffff&text_color=0a0a0a&primary_color=0a0a0a`}
                  style={{ minWidth: "0", width: "100%", maxWidth: "100%", height: "720px" }}
                />
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="border border-navy bg-white p-8 md:p-12"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <Field
                    label={t("contact.name")}
                    required
                    testid="contact-name"
                    value={form.name}
                    onChange={(v) => setForm({ ...form, name: v })}
                  />
                  <Field
                    label={t("contact.email")}
                    required
                    type="email"
                    testid="contact-email-field"
                    value={form.email}
                    onChange={(v) => setForm({ ...form, email: v })}
                  />
                  <Field
                    label={t("contact.phone")}
                    testid="contact-phone-field"
                    value={form.phone}
                    onChange={(v) => setForm({ ...form, phone: v })}
                  />
                  <Field
                    label={t("contact.company")}
                    testid="contact-company-field"
                    value={form.company}
                    onChange={(v) => setForm({ ...form, company: v })}
                  />
                </div>

                <div className="mt-8">
                  <label htmlFor="contact-message" className="eyebrow text-ink mb-2 block">
                    {t("contact.message")} <span className="text-navy">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    required
                    rows={4}
                    aria-label={t("contact.message")}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full bg-transparent border-b border-navy/30 pb-3 text-base focus:outline-none focus:border-navy resize-none"
                  />
                </div>

                <div className="mt-10 flex items-center justify-end">
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit-btn"
                    className="btn-editorial btn-editorial-primary disabled:opacity-60"
                  >
                    {sending ? t("contact.sending") : t("contact.submit")}
                    <ArrowRight size={16} />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, type = "text", testid, value, onChange }) {
  return (
    <label className="flex flex-col">
      <span className="eyebrow text-ink mb-1">
        {label}
        {required && <span className="ml-1 text-navy">*</span>}
      </span>
      <input
        required={required}
        type={type}
        data-testid={`${testid}-input`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border-b border-navy/30 pb-2 focus:outline-none focus:border-navy"
      />
    </label>
  );
}
